"use client"

import { useState } from "react"
import { toast, ToastContainer } from "react-toastify"
import { searchRecords, updateRecord, bulkUpdateRecords, createScheduleRmsTicket } from "../../../services/AdminService"
import "react-toastify/dist/ReactToastify.css"

const UpdateRecords = () => {
  // Simplified search criteria - only Area Code and Mobile
  const [searchCriteria, setSearchCriteria] = useState({
    areaCode: "",
    mobile: "",
  })

  const [records, setRecords] = useState([])
  const [filteredRecords, setFilteredRecords] = useState([])
  const [selectedRecords, setSelectedRecords] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [showBulkUpdate, setShowBulkUpdate] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const [searchedByAreaCode, setSearchedByAreaCode] = useState(false) // Track search type
  const [showTicketCreation, setShowTicketCreation] = useState(false)
  const [isCreatingTicket, setIsCreatingTicket] = useState(false)
  const [ticketFormData, setTicketFormData] = useState({
    description: "Schedule RMS",
    complaintID: 106, //  Added complaintID to form state
  })
  const [createdTickets, setCreatedTickets] = useState([])

  // Bulk update form state
  const [bulkUpdateData, setBulkUpdateData] = useState({
    planStart: "",
    planEnd: "",
    contractType: "",
    rmsBalance: "",
    areaCode: "",
  })

  // Individual update form state
  const [updateFormData, setUpdateFormData] = useState({
    id: "",
    planStart: "",
    planEnd: "",
    contractType: "",
    deviceId: "",
    areaCode: "",
    rmsBalance: "",
    userName: "",
    email: "",
    mobile: "",
  })

  // Get session data
  const getClientId = () => {
    let clientId = sessionStorage.getItem("clientId")
    if (!clientId) {
      // Try alternative storage keys
      clientId =
        sessionStorage.getItem("client_id") || localStorage.getItem("clientId") || localStorage.getItem("client_id")
    }
    return clientId
  }

  const clientId = getClientId()
  const currentUserId = sessionStorage.getItem("userId")

  // Get common values from filtered records
  const getCommonValues = () => {
    if (filteredRecords.length === 0) return null

    const areaCodes = [...new Set(filteredRecords.map((record) => record.areaCode))]
    const productNames = [...new Set(filteredRecords.map((record) => record.productName))]
    const contractTypes = [...new Set(filteredRecords.map((record) => record.contractType))]

    return {
      areaCode: areaCodes.length === 1 ? areaCodes[0] : null,
      productName: productNames.length === 1 ? productNames[0] : null,
      contractType: contractTypes.length === 1 ? contractTypes[0] : null,
      hasMultipleAreaCodes: areaCodes.length > 1,
      hasMultipleProducts: productNames.length > 1,
      hasMultipleContractTypes: contractTypes.length > 1,
      areaCodes: areaCodes,
      productNames: productNames,
      contractTypes: contractTypes,
    }
  }

  // Search function - simplified for Area Code and Mobile only
  const handleSearch = async () => {
    if (!searchCriteria.areaCode && !searchCriteria.mobile) {
      toast.warning("Please enter Area Code or Mobile number to search.")
      return
    }

    setSearchedByAreaCode(!!searchCriteria.mobile && !!searchCriteria.areaCode)

    setIsLoading(true)
    try {
      const result = await searchRecords({
        ...searchCriteria,
        clientId: Number.parseInt(clientId),
        userId: "", // Empty since we removed it
        deviceId: "", // Empty since we removed it
        userName: "", // Empty since we removed it
      })

      if (result.status) {
        setRecords(result.data || [])
        setFilteredRecords(result.data || [])

        if (result.data && result.data.length > 0) {
          toast.success(`Found ${result.data.length} records.`)
        } else {
          toast.info("No records found.")
        }
      } else {
        toast.error(result.message || "Search failed. Please try again.")
        setRecords([])
        setFilteredRecords([])
      }
    } catch (error) {
      console.error("Search error:", error)
      toast.error("Search failed. Please check your connection.")
      setRecords([])
      setFilteredRecords([])
    } finally {
      setIsLoading(false)
    }
  }

  // Record selection functions
  const handleRecordSelection = (recordId, isSelected) => {
    if (isSelected) {
      setSelectedRecords([...selectedRecords, recordId])
    } else {
      setSelectedRecords(selectedRecords.filter((id) => id !== recordId))
    }
  }

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedRecords(filteredRecords.map((record) => record.id))
    } else {
      setSelectedRecords([])
    }
  }

  // Bulk update functions
  const checkBulkUpdateEligibility = () => {
    if (selectedRecords.length < 2) {
      toast.warning("Please select at least 2 records for bulk update.")
      return false
    }
    const selectedRecordData = filteredRecords.filter((record) => selectedRecords.includes(record.id))
    const areaCodes = [...new Set(selectedRecordData.map((record) => record.areaCode))]
    if (areaCodes.length > 1) {
      toast.warning("Bulk update requires records from the same area code.")
      return false
    }
    return true
  }

  const openBulkUpdate = () => {
    if (!checkBulkUpdateEligibility()) return
    const selectedRecordData = filteredRecords.filter((record) => selectedRecords.includes(record.id))
    setBulkUpdateData({
      planStart: "",
      planEnd: "",
      contractType: selectedRecordData[0]?.contractType || "",
      rmsBalance: "",
      areaCode: selectedRecordData[0]?.areaCode || "",
    })
    setShowBulkUpdate(true)
  }

  const handleBulkUpdate = async () => {
    if (!bulkUpdateData.planStart || !bulkUpdateData.planEnd) {
      toast.error("Plan start and end dates are required.")
      return
    }
    setIsUpdating(true)
    try {
      const result = await bulkUpdateRecords({
        recordIds: selectedRecords,
        updateData: bulkUpdateData,
        clientId: Number.parseInt(clientId),
        updatedBy: Number.parseInt(currentUserId),
      })
      if (result.status) {
        if (result.data && result.data.failed_count > 0) {
          toast.warning(`Updated ${result.data.updated_count} records. ${result.data.failed_count} failed.`)
        } else {
          toast.success(`Successfully updated ${selectedRecords.length} records.`)
        }
        setShowBulkUpdate(false)
        setSelectedRecords([])
        handleSearch()
      } else {
        toast.error(`Update failed: ${result.message}`)
      }
    } catch (error) {
      console.error("Bulk update error:", error)
      toast.error("Update failed. Please try again.")
    } finally {
      setIsUpdating(false)
    }
  }

  // Individual update functions
  const openUpdateForm = (record) => {
    setUpdateFormData({
      id: record.id,
      planStart: record.planStart ? record.planStart.substring(0, 10) : "",
      planEnd: record.planEnd ? record.planEnd.substring(0, 10) : "",
      contractType: record.contractType || "",
      deviceId: record.deviceID || "",
      areaCode: record.areaCode || "",
      rmsBalance: record.rmsBalance || "",
      userName: record.userName || "",
      email: record.email || "",
      mobile: record.mobile || "",
    })
    setEditingRecord(record.id)
  }

  const handleIndividualUpdate = async () => {
    if (!updateFormData.planStart || !updateFormData.planEnd) {
      toast.error("Plan start and end dates are required.")
      return
    }
    setIsUpdating(true)
    try {
      const result = await updateRecord({
        ...updateFormData,
        clientId: Number.parseInt(clientId),
        updatedBy: Number.parseInt(currentUserId),
      })
      if (result.status) {
        toast.success("Record updated successfully.")
        setEditingRecord(null)
        handleSearch()
      } else {
        toast.error(`Update failed: ${result.message}`)
      }
    } catch (error) {
      console.error("Update error:", error)
      toast.error("Update failed. Please try again.")
    } finally {
      setIsUpdating(false)
    }
  }

  // NEW: Check if Schedule RMS button should be shown/enabled
  const getScheduleRmsButtonStatus = () => {
    // Only show for mobile number + area code combination searches
    if (!searchedByAreaCode) {
      return { show: false, enabled: false, message: "" }
    }

    if (selectedRecords.length === 0) {
      return { show: true, enabled: false, message: "Select devices to schedule RMS" }
    }

    const selectedRecordData = filteredRecords.filter((record) => selectedRecords.includes(record.id))
    const userIds = [...new Set(selectedRecordData.map((record) => record.UserID))]
    const areaCodes = [...new Set(selectedRecordData.map((record) => record.areaCode))]

    // Check if all selected records belong to same user and area code
    if (userIds.length > 1) {
      return { show: true, enabled: false, message: "Select devices from the same user only" }
    }

    if (areaCodes.length > 1) {
      return { show: true, enabled: false, message: "Select devices from the same area code only" }
    }

    // Check if ALL records from the same area code are selected
    const areaCodeOfSelected = areaCodes[0]
    const allRecordsInAreaCode = filteredRecords.filter((record) => record.areaCode === areaCodeOfSelected)
    const allRecordsSelected = allRecordsInAreaCode.every((record) => selectedRecords.includes(record.id))

    if (!allRecordsSelected) {
      return {
        show: true,
        enabled: false,
        message: `Select all ${allRecordsInAreaCode.length} devices from area code ${areaCodeOfSelected}`,
      }
    }

    return { show: true, enabled: true, message: "" }
  }

  const openAreaBasedTicketCreation = () => {
    const buttonStatus = getScheduleRmsButtonStatus()
    if (!buttonStatus.enabled) {
      toast.warning(buttonStatus.message)
      return
    }

    setTicketFormData({
      description: "Schedule RMS",
      complaintID: 106, //  Initialize complaintID in form data
    })
    setShowTicketCreation(true)
  }

  const handleAreaBasedTicketCreation = async () => {
    if (!ticketFormData.description.trim()) {
      toast.error("Please enter ticket description.")
      return
    }

    if (!ticketFormData.complaintID) {
      toast.error("Please enter complaint ID.")
      return
    }

    const validClientId = getClientId()
    if (!validClientId) {
      toast.error("Client ID is required. Please login again.")
      console.error("Client ID not found in session storage")
      return
    }

    if (!currentUserId) {
      toast.error("User ID is required. Please login again.")
      return
    }

    setIsCreatingTicket(true)

    const selectedRecordData = filteredRecords.filter((record) => selectedRecords.includes(record.id))
    const userRecord = selectedRecordData[0]
    const areaCode = userRecord.areaCode
    const deviceIDs = selectedRecordData.map((record) => record.deviceID)

    try {
      const ticketPayload = {
        userId: Number.parseInt(userRecord.UserID),
        deviceIDs: deviceIDs,
        areaCode: areaCode.toString(),
        description: ticketFormData.description,
        ClientId: Number.parseInt(validClientId),
        createdBy: Number.parseInt(currentUserId),
        complaintID: Number.parseInt(ticketFormData.complaintID),
        productID: Number.parseInt(userRecord.productID || 33),
        rmsBalance: userRecord.rmsBalance, // Use the area's RMS balance as-is
        deviceCount: deviceIDs.length, // For reference only, not for balance calculation
      }

      console.log("[v0] Creating Schedule RMS Ticket with payload:", ticketPayload)

      const ticketResult = await createScheduleRmsTicket(ticketPayload)

      console.log("[v0] Ticket creation result:", ticketResult)

      if (ticketResult.status) {
        const firstTicket = ticketResult.created_tickets && ticketResult.created_tickets[0]
        const ticketId = firstTicket ? firstTicket.ticket_id : "Unknown"

        const newTicket = {
          id: ticketId,
          userName: userRecord.userName,
          mobile: userRecord.mobile,
          areaCode: areaCode,
          deviceCount: deviceIDs.length,
          deviceIDs: deviceIDs,
          deviceDisplay: `${deviceIDs.length}(${deviceIDs.join(", ")})`,
          description: ticketFormData.description,
          createdAt: new Date().toLocaleString(),
          status: "Open",
          rmsBalance: userRecord.rmsBalance, // Area-based balance
        }
        setCreatedTickets([newTicket, ...createdTickets])

        toast.success(
          `Schedule RMS Ticket created successfully for Area ${areaCode} with ${deviceIDs.length} devices!`,
        )

        setShowTicketCreation(false)
        setSelectedRecords([])
        setTicketFormData({ description: "Schedule RMS", complaintID: 106 })
      } else {
        toast.error(ticketResult.message || "Failed to create Schedule RMS ticket")
      }
    } catch (error) {
      console.error("[v0] Schedule RMS ticket creation error:", error)
      if (error.message && error.message.includes("Client ID")) {
        toast.error("Authentication error. Please login again.")
      } else {
        toast.error(`Failed to create Schedule RMS ticket: ${error.message || "Unknown error"}`)
      }
    } finally {
      setIsCreatingTicket(false)
    }
  }
  const getSelectedAreaInfo = () => {
    if (selectedRecords.length === 0) return null
    const selectedRecordData = filteredRecords.filter((record) => selectedRecords.includes(record.id))
    const areaCode = selectedRecordData[0]?.areaCode
    const userName = selectedRecordData[0]?.userName
    const deviceIDs = selectedRecordData.map((record) => record.deviceID)
    return { areaCode, userName, deviceIDs }
  }

  const resetSearch = () => {
    setSearchCriteria({
      areaCode: "",
      mobile: "",
    })
    setRecords([])
    setFilteredRecords([])
    setSelectedRecords([])
    setEditingRecord(null)
    setSearchedByAreaCode(false)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    try {
      return new Date(dateString).toLocaleDateString()
    } catch (error) {
      return "Invalid Date"
    }
  }

  const selectedAreaInfo = getSelectedAreaInfo()
  const commonValues = getCommonValues()
  const scheduleRmsButtonStatus = getScheduleRmsButtonStatus()

  return (
    <div className="container-fluid mt-4">
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <h5 className="text-primary mb-3">Update Records & Schedule RMS Tickets</h5>
          <div className="alert alert-info">
            <p className="mb-0">
              Search for existing records using Area Code or Mobile number and create area-based "Schedule RMS" tickets.
            </p>{" "}
            <p className="mb-0"> Use Area Code search to enable RMS scheduling for all devices in that area.</p>
          </div>
        </div>
      </div>

      {/* Created Tickets List */}
      {createdTickets.length > 0 && (
        <div className="card mb-4 border-success">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">
              <i className="fas fa-ticket-alt me-2"></i>
              Recently Created Schedule RMS Tickets ({createdTickets.length})
            </h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    {/* <th>Ticket ID</th> */}
                    <th>User</th>
                    <th>Area Code</th>
                    <th>Devices</th>
                    <th>Type</th>
                    <th>Created</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {createdTickets.map((ticket, index) => (
                    <tr key={index}>
                      {/* <td>
                        <span className="badge bg-success fs-6">#CBR-{ticket.ticketId}</span>
                      </td> */}
                      <td>
                        <div>
                          <strong>{ticket.userName}</strong>
                          <br />
                          <small className="text-muted">{ticket.mobile}</small>
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-primary">{ticket.areaCode}</span>
                      </td>
                      <td>
                        <span className="badge bg-info">{ticket.deviceCount} devices</span>
                        <br />
                        <small className="text-muted">{ticket.deviceIDs.join(", ")}</small>
                      </td>
                      <td>
                        <span className="badge bg-warning text-dark">Schedule RMS</span>
                      </td>
                      <td>
                        <small>{ticket.createdAt}</small>
                      </td>
                      <td>
                        <span className="badge bg-success">{ticket.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Simplified Search Section - One Line */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Search Records</h5>
        </div>
        <div className="card-body">
          <div className="row align-items-end">
            <div className="col-md-4">
              <label className="form-label">Area Code</label>
              <input
                type="text"
                className="form-control"
                value={searchCriteria.areaCode}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, areaCode: e.target.value })}
                placeholder="Enter Area Code"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Mobile Number</label>
              <input
                type="text"
                className="form-control"
                value={searchCriteria.mobile}
                onChange={(e) => setSearchCriteria({ ...searchCriteria, mobile: e.target.value })}
                placeholder="Enter Mobile Number"
              />
            </div>
            <div className="col-md-4">
              <div className="d-flex gap-2">
                <button className="btn btn-primary flex-fill" onClick={handleSearch} disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Searching...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-search me-1"></i>
                      Search
                    </>
                  )}
                </button>
                <button className="btn btn-outline-secondary" onClick={resetSearch}>
                  <i className="fas fa-redo me-1"></i>
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {filteredRecords.length > 0 && (
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Search Results ({filteredRecords.length} records)</h5>
            <div>
              {selectedRecords.length > 0 && (
                <>
                  <button className="btn btn-success me-2" onClick={openBulkUpdate} disabled={isUpdating}>
                    <i className="fas fa-edit me-1"></i>
                    Bulk Update ({selectedRecords.length})
                  </button>
                  {scheduleRmsButtonStatus.show && (
                    <button
                      className={`btn ${scheduleRmsButtonStatus.enabled ? "btn-warning" : "btn-outline-warning"}`}
                      onClick={openAreaBasedTicketCreation}
                      disabled={isCreatingTicket || !scheduleRmsButtonStatus.enabled}
                      title={scheduleRmsButtonStatus.message}
                    >
                      <i className="fas fa-calendar-alt me-1"></i>
                      Schedule RMS
                      {selectedAreaInfo && scheduleRmsButtonStatus.enabled && (
                        <span className="ms-1">({selectedRecords.length} devices)</span>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Common Values Display */}
          {commonValues && (
            <div className="card-body border-bottom bg-light">
              <div className="row">
                <div className="col-12">
                  <h6 className="text-muted mb-2">
                    <i className="fas fa-info-circle me-2"></i>
                    Record Information
                  </h6>
                  <div className="d-flex flex-wrap gap-3">
                    {commonValues.areaCode && (
                      <div className="d-flex align-items-center">
                        <strong className="me-2">Area Code:</strong>
                        <span className="badge bg-primary fs-6">{commonValues.areaCode}</span>
                      </div>
                    )}
                    {commonValues.productName && (
                      <div className="d-flex align-items-center">
                        <strong className="me-2">Product:</strong>
                        <span className="badge bg-info fs-6">{commonValues.productName}</span>
                      </div>
                    )}
                    {commonValues.contractType && (
                      <div className="d-flex align-items-center">
                        <strong className="me-2">Contract Type:</strong>
                        <span className="badge bg-secondary fs-6">{commonValues.contractType}</span>
                      </div>
                    )}
                  </div>
                  {/* Show button status message if needed */}
                  {scheduleRmsButtonStatus.show &&
                    !scheduleRmsButtonStatus.enabled &&
                    scheduleRmsButtonStatus.message && (
                      <div className="mt-2">
                        <small className="text-warning">
                          <i className="fas fa-info-circle me-1"></i>
                          {scheduleRmsButtonStatus.message}
                        </small>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        checked={selectedRecords.length === filteredRecords.length && filteredRecords.length > 0}
                      />
                    </th>
                    <th>User Name</th>
                    <th>Mobile</th>
                    <th>Device ID</th>
                    {/* Show these columns only if values are not common */}
                    {commonValues?.hasMultipleAreaCodes && <th>Area Code</th>}
                    {commonValues?.hasMultipleProducts && <th>Product</th>}
                    {commonValues?.hasMultipleContractTypes && <th>Contract Type</th>}
                    <th>Plan Start</th>
                    <th>Plan End</th>
                    <th>RMS Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <tr key={record.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedRecords.includes(record.id)}
                          onChange={(e) => handleRecordSelection(record.id, e.target.checked)}
                        />
                      </td>
                      <td>
                        <strong>{record.userName}</strong>
                      </td>
                      <td>{record.mobile}</td>
                      <td>
                        <span className="badge bg-info">{record.deviceID}</span>
                      </td>
                      {/* Show these columns only if values are not common */}
                      {commonValues?.hasMultipleAreaCodes && (
                        <td>
                          <span className="badge bg-primary">{record.areaCode}</span>
                        </td>
                      )}
                      {commonValues?.hasMultipleProducts && <td>{record.productName}</td>}
                      {commonValues?.hasMultipleContractTypes && <td>{record.contractType}</td>}
                      <td>{formatDate(record.planStart)}</td>
                      <td>{formatDate(record.planEnd)}</td>
                      <td>
                        <span className="badge bg-success">{record.rmsBalance}</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openUpdateForm(record)}
                          disabled={isUpdating}
                        >
                          <i className="fas fa-edit me-1"></i>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showTicketCreation && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <i className="fas fa-calendar-alt me-2"></i>
                  Schedule RMS Ticket
                  {selectedAreaInfo && (
                    <span className="ms-2">
                      (Area: {selectedAreaInfo.areaCode} - {selectedRecords.length} devices)
                    </span>
                  )}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowTicketCreation(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <h6 className="alert-heading">
                    <i className="fas fa-info-circle me-2"></i>
                    Schedule RMS for Area Code
                  </h6>
                  <p className="mb-0">
                    Creating RMS schedule for Area Code <strong>{selectedAreaInfo?.areaCode}</strong> covering{" "}
                    <strong>{selectedRecords.length} devices</strong> for user{" "}
                    <strong>{selectedAreaInfo?.userName}</strong>.
                  </p>
                </div>

                {selectedAreaInfo && (
                  <div className="row">
                    <div className="col-12">
                      <label className="form-label fw-bold">
                        <i className="fas fa-mobile-alt me-2"></i>
                        Devices to Schedule ({selectedAreaInfo.deviceIDs.length})
                      </label>
                      <div className="card bg-light">
                        <div className="card-body">
                          <div className="row">
                            {selectedAreaInfo.deviceIDs.map((deviceID, index) => (
                              <div key={index} className="col-md-2 col-lg-2 mb-2">
                                <div className="d-flex align-items-center justify-content-center">
                                  <i className="fas fa-mobile text-primary me-2"></i>
                                  <span className="badge bg-info fs-6">{deviceID}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="mt-3 text-center">
                            <small className="text-muted">
                              <strong>User:</strong> {selectedAreaInfo.userName} |<strong> Area Code:</strong>{" "}
                              {selectedAreaInfo.areaCode}
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTicketCreation(false)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleAreaBasedTicketCreation}
                  disabled={isCreatingTicket}
                >
                  {isCreatingTicket ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating Ticket...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus-circle me-2"></i>
                      Create Schedule RMS Ticket
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Individual Update Modal */}
      {editingRecord && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Update Record</h5>
                <button type="button" className="btn-close" onClick={() => setEditingRecord(null)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">User Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={updateFormData.userName}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, userName: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={updateFormData.email}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, email: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Mobile</label>
                    <input
                      type="text"
                      className="form-control"
                      value={updateFormData.mobile}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, mobile: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Device ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={updateFormData.deviceId}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, deviceId: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contract Type</label>
                    <select
                      className="form-control"
                      value={updateFormData.contractType}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, contractType: e.target.value })}
                    >
                      <option value="">Select Contract Type</option>
                      <option value="AMC">AMC</option>
                      <option value="WRT">WRT</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Area Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={updateFormData.areaCode}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, areaCode: e.target.value })}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Plan Start Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={updateFormData.planStart}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, planStart: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Plan End Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={updateFormData.planEnd}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, planEnd: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">RMS Balance</label>
                    <input
                      type="number"
                      className="form-control"
                      value={updateFormData.rmsBalance}
                      onChange={(e) => setUpdateFormData({ ...updateFormData, rmsBalance: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingRecord(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleIndividualUpdate}
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Updating...
                    </>
                  ) : (
                    "Update Record"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkUpdate && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Bulk Update Records ({selectedRecords.length} records)</h5>
                <button type="button" className="btn-close" onClick={() => setShowBulkUpdate(false)}></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-info">
                  <strong>Note:</strong> This will update all selected records with the same values. Only fill in the
                  fields you want to update.
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label">Plan Start Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={bulkUpdateData.planStart}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, planStart: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Plan End Date *</label>
                    <input
                      type="date"
                      className="form-control"
                      value={bulkUpdateData.planEnd}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, planEnd: e.target.value })}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Contract Type</label>
                    <select
                      className="form-control"
                      value={bulkUpdateData.contractType}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, contractType: e.target.value })}
                    >
                      <option value="">Keep Current</option>
                      <option value="AMC">AMC</option>
                      <option value="WRT">WRT</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">RMS Balance</label>
                    <input
                      type="number"
                      className="form-control"
                      value={bulkUpdateData.rmsBalance}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, rmsBalance: e.target.value })}
                      placeholder="Leave empty to keep current"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Area Code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={bulkUpdateData.areaCode}
                      onChange={(e) => setBulkUpdateData({ ...bulkUpdateData, areaCode: e.target.value })}
                      placeholder="Leave empty to keep current"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBulkUpdate(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-success" onClick={handleBulkUpdate} disabled={isUpdating}>
                  {isUpdating ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Updating...
                    </>
                  ) : (
                    `Update ${selectedRecords.length} Records`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Results Message */}
      {!isLoading && records.length === 0 && (
        <div className="text-center mt-5">
          <div className="alert alert-light">
            <h5>No Records Found</h5>
            <p>Use Area Code search to enable RMS scheduling, or Mobile number search to find specific records.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default UpdateRecords
